import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Box, Fab, Tooltip, Typography } from '@material-ui/core';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import InfiniteScrollTableView from '../../../components/InfiniteScrollTableView';

import { BASE_URL } from '../../../routes';
import { CitationList } from '../../../store/modules/citation/types';
import TableEmptyState from '../../../components/TableView/TableEmptyState';

import { FilterCondition, IColumn, Order } from '../../../store/modules/application/types';

declare module '@material-ui/core/styles/withStyles' {
    // Augment the BaseCSSProperties so that we can control jss-rtl
    interface BaseCSSProperties {
        /*
         * Used to control if the rule-set should be affected by rtl transformation
         */
        flip?: boolean;
    }
}

type CitationProps = {
    citationList: CitationList;
    isLoading: boolean;
    skip: number;
    setSkip: Function;
    limit: number;
    hasMore: boolean;
    citationCount: number;
    sortOrder: Order;
    sortBy: string;
    setSortOrder: Function;
    setSortColumn: Function;
    scrollToTopTrigger?: Object;
    listFilters?: FilterCondition[];
};

const componentStyles = makeStyles(() =>
    createStyles({
        root: {
            padding: '0px 40px 40px 40px',
        },
        tablePaginationInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
        },

        container: {
            display: 'flex',
            width: '100%',
            backgroundColor: 'white',
        },
        fab: {
            position: 'absolute',
            bottom: 55,
            left: 'calc( 100vw - 100px)',
            zIndex: 3,
            backgroundColor: 'white',
        },
        count: {
            color: '#484848',
            fontSize: 14,
            letterSpacing: 0.1,
            lineHeight: '20px',
            height: '20px',
        },
        metadataContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    }),
);

const columns: IColumn[] = [
    {
        id: 'citationNumber|string',
        label: 'Citation Number',
        align: 'left',
        maxWidth: 300,
        field: 'citationNumber',
        sortFields: ['citationNumber'],
    },
    {
        id: 'type|string',
        label: 'Citation Type',
        align: 'left',
        maxWidth: 300,
        field: 'type',
        sortFields: ['type'],
    },
    {
        id: 'licensee|string',
        label: 'Licensee',
        align: 'left',
        maxWidth: 300,
        field: 'licensee',
        sortFields: ['licensee'],
    },
    {
        id: 'licenseeType|string',
        label: 'Licensee Type',
        align: 'left',
        maxWidth: 300,
        field: 'licenseeType',
        sortFields: ['licenseeType'],
    },
    {
        id: 'licenseCount|number',
        label: 'Total Number of Licenses',
        align: 'left',
        maxWidth: 300,
        field: 'licenseCount',
        noSort: true,
    },
    {
        id: 'citationDate|date',
        label: 'Citation Date',
        align: 'left',
        maxWidth: 300,
        field: 'citationDate',
        sortFields: ['citationDate'],
    },
    {
        id: 'status|string',
        label: 'Status',
        align: 'left',
        maxWidth: 300,
        field: 'status',
        sortFields: ['status'],
    },
];

const CitationInfiniteList: FunctionComponent<CitationProps> = (props) => {
    const classes = componentStyles();
    const history = useHistory();
    const [scrollTop, setScrollTop] = useState<number>(0);

    const {
        citationList,
        skip,
        citationCount,
        hasMore,
        setSkip,
        sortBy,
        sortOrder,
        setSortOrder,
        setSortColumn,
        scrollToTopTrigger,
        listFilters,
    } = props;
    useEffect(() => {
        if (skip === 0) {
            setScrollTop(0);
        }
    }, [skip]);

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };
    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/citation/${id}/detail`);
    };

    const citationListFormatted = citationList?.map((element) => {
        return {
            ...element,
            type: element.type.name,
            licensee: element.person.name,
            licenseeType: element?.person.type?.name,
            licenseCount: element.licenses?.length,
        };
    });

    return (
        <Box className={`${classes.root} verticalFillContainer`}>
            <Box className={classes.metadataContainer}>
                <Box className={classes.tablePaginationInfo}>
                    <Typography className={classes.count}>
                        {listFilters?.length ? `${citationCount} Results Found` : ''}
                    </Typography>
                </Box>
            </Box>
            <Box className={`${classes.container} verticalFillContent`}>
                {citationList.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={citationListFormatted}
                        count={citationCount}
                        onClickRow={onClickRow}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        sortOrder={sortOrder}
                        sortBy={sortBy}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                        scrollToTopTrigger={scrollToTopTrigger}
                    />
                ) : (
                    <TableEmptyState
                        title="No active citations."
                        message="All Citations will be stored here"
                    ></TableEmptyState>
                )}
            </Box>

            {scrollTop > 20 && (
                <Tooltip title="Back to Top" placement="top">
                    <Fab
                        size="small"
                        className={classes.fab}
                        aria-label="back-to-top"
                        onClick={() => {
                            setScrollTop(0);
                        }}
                    >
                        <BackToTopArrow color="primary" />
                    </Fab>
                </Tooltip>
            )}
        </Box>
    );
};

export default CitationInfiniteList;
